import { AlwaysOnMode, CameraMode, CamNameSetting, GPS, MobileVideo, SdCycle } from '@hunting-nature/shared-core/enums/remote-configs.enum';
import type { Duration } from '@hunting-nature/shared-core/interfaces/remote-control.interface';
import type { RemoteControlResponse } from '@hunting-nature/shared-core/types/remote-control-response.type';
import type { SubscriptionPlan } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { SubscriptionPlanValues } from '@hunting-nature/shared-core/types/subscription-plan.type';
import { createTime } from '@hunting-nature/shared-core/utils/create-time';

import type { VideoSettingsState } from '../../../../../../libs/shared-core/src/lib/interfaces/video-settings.interface';
import type { RemoteControlState } from '../../pages/cameras/redux/remote-control.reducer';
import { parseDelaySettingTime } from '../helper/camera.helper';
import { getPaymentTypeBySubscription, parseTimelapseDuration, parseTimerSetting, SETTING_OFF } from '../helper/remote-control.helper';
import { PaymentType } from '@hunting-nature/shared-core/enums/payment-type.enum';

export class RemoteControlResponseMapper {
  private constructor() {
    /* empty */
  }

  // eslint-disable-next-line max-lines-per-function
  public static readonly toPartialRemoteControlReduxState = (response: RemoteControlResponse): Partial<RemoteControlState> => {
    const imei = response.imei;

    const cameraModePending = !!response.states.pendingState?.cameraMode;

    const cameraMode = response.states.pendingState?.cameraMode ?? response.states.acknowledgedState?.cameraMode;
    const cameraModeOff = cameraMode === CameraMode.OFF;

    const sensitivityPending = !!response.states.pendingState?.sensitivity;
    const sensitivity = response.states.pendingState?.sensitivity ?? response.states.acknowledgedState?.sensitivity;

    const nightModePending = !!response.states.pendingState?.nightMode;
    const nightMode = response.states.pendingState?.nightMode ?? response.states.acknowledgedState?.nightMode;

    const maxNumberPerHourPending = !!response.states.pendingState?.maxNumPerHour;
    const maxNumberPerHour = response.states.pendingState?.maxNumPerHour ?? response.states.acknowledgedState?.maxNumPerHour;

    const pictureNumberToSendPending = !!response.states.pendingState?.picNumberToSend;
    const pictureNumberToSend = Number(response.states.pendingState?.picNumberToSend ?? response.states.acknowledgedState?.picNumberToSend);

    const snapshotNumberResponsePending = !!response.states.pendingState?.snapNum;
    const snapshotResponseNumber = Number(response.states.pendingState?.snapNum ?? response.states.acknowledgedState?.snapNum);
    const snapshotResponsePendingNumber = Number(response.states.pendingState?.snapNum ?? 0);
    const snapshotResponseAcknowledgedNumber = Number(response.states.acknowledgedState?.snapNum);
    const quickPhotoEnabled = snapshotResponseNumber > 5;
    const quickPhotoEnabledPending =
      snapshotNumberResponsePending &&
      Math.max(snapshotResponseAcknowledgedNumber, snapshotResponsePendingNumber) > 5 &&
      Math.min(snapshotResponseAcknowledgedNumber, snapshotResponsePendingNumber) <= 5;
    const snapshotNumberPending =
      (snapshotNumberResponsePending && !quickPhotoEnabledPending) ||
      (snapshotNumberResponsePending && quickPhotoEnabledPending && Math.abs(snapshotResponsePendingNumber - snapshotResponseAcknowledgedNumber) !== 3);
    const snapshotNumber = quickPhotoEnabled ? snapshotResponseNumber - 3 : snapshotResponseNumber;

    const sendingPictureSizePending = !!response.states.pendingState?.sendingPicSize;
    const sendingPictureSize = response.states.pendingState?.sendingPicSize ?? response.states.acknowledgedState?.sendingPicSize;

    const imageSizePending = !!response.states.pendingState?.imageSize;
    const imageSize = response.states.pendingState?.imageSize ?? response.states.acknowledgedState?.imageSize;

    const timerSettingPending = !!response.states.pendingState?.timerSetting;
    const timerSetting = response.states.pendingState?.timerSetting ?? response.states.acknowledgedState?.timerSetting;
    const timerSettingOff = timerSetting === SETTING_OFF;

    const delaySettingPending = !!response.states.pendingState?.delaySetting;
    const delaySettingString = response.states.pendingState?.delaySetting ?? response.states.acknowledgedState?.delaySetting;
    let delaySettingsDuration: Duration | undefined = parseDelaySettingTime(delaySettingString);
    const delaySettingOff = delaySettingsDuration === undefined;
    if (!delaySettingsDuration) {
      delaySettingsDuration = { hours: 0, minutes: 0, seconds: 0 };
    }

    const timelapseSettingPending = !!response.states.pendingState?.timelapseSetting;
    const timelapseSetting =
      response.states.pendingState?.timelapseSetting?.timelapseSetting ?? response.states.acknowledgedState?.timelapseSetting?.timelapseSetting;
    const timelapseSettingOff = timelapseSetting === SETTING_OFF;
    const motionSensorSetting = response.states.pendingState?.timelapseSetting?.PIR ?? response.states.acknowledgedState?.timelapseSetting?.PIR;

    const controlPicture1Pending = !!response.states.pendingState?.controlPic?.controlPic1;
    const controlPicture1Value = response.states.pendingState?.controlPic?.controlPic1 ?? response.states.acknowledgedState?.controlPic?.controlPic1;
    const controlPicture1Off = controlPicture1Value === SETTING_OFF;
    const controlPicture1 = controlPicture1Value && !controlPicture1Off ? createTime(controlPicture1Value) : undefined;

    const controlPicture2Pending = !!response.states.pendingState?.controlPic?.controlPic2;
    const controlPicture2Value = response.states.pendingState?.controlPic?.controlPic2 ?? response.states.acknowledgedState?.controlPic?.controlPic2;
    const controlPicture2Off = controlPicture2Value === SETTING_OFF;
    const controlPicture2 = controlPicture2Value && !controlPicture2Off ? createTime(controlPicture2Value) : undefined;

    const sendReportIntervalPending = !!response.states.pendingState?.sendReportInterval;
    const sendReportInterval = response.states.pendingState?.sendReportInterval ?? response.states.acknowledgedState?.sendReportInterval;
    const sendReportIntervalOff = sendReportInterval === SETTING_OFF;

    const instantModePending = !!response.states.pendingState?.instantMode;
    const instantMode = response.states.pendingState?.instantMode ?? response.states.acknowledgedState?.instantMode;
    const instantModeOff = (instantMode as AlwaysOnMode) === AlwaysOnMode.Off;

    const cameraNameSettingPending = !!response.states.pendingState?.camNameSetting;
    const cameraNameSetting = response.states.pendingState?.camNameSetting ?? response.states.acknowledgedState?.camNameSetting;
    const cameraNameString = response.states.pendingState?.camNameString?.trim() ?? response.states.acknowledgedState?.camNameString?.trim();
    const cameraNameSettingOff = (cameraNameSetting as CamNameSetting) === CamNameSetting.OFF;

    const secaTrackPending = !!response.states.pendingState?.gpsPulse;
    const secaTrack = response.states.pendingState?.gpsPulse ?? response.states.acknowledgedState?.gpsPulse;
    const secaTrackActive = response.states.pendingState?.gpsPulseActive ?? response.states.acknowledgedState?.gpsPulseActive;
    const secaTrackOff = (secaTrack as GPS) === GPS.OFF;

    const gpsPending = !!response.states.pendingState?.gps;
    const gps = response.states.pendingState?.gps ?? response.states.acknowledgedState?.gps;
    const gpsOff = gps === GPS.OFF;

    const sdCyclePending = !!response.states.pendingState?.cycle;
    const sdCycleValue = response.states.pendingState?.cycle ?? response.states.acknowledgedState?.cycle;
    const sdCycle = sdCycleValue == undefined ? undefined : (sdCycleValue as SdCycle);
    const sdCycleOff = sdCycle !== SdCycle.ON;

    const pinCodePending = response.states.pendingState?.pinCode != undefined;

    const resetPending = !!response.states.pendingState?.reset;

    const subscription_name = response.states.pendingState?.subscription_name ?? response.states.acknowledgedState?.subscription_name;

    const paymentType = getPaymentTypeBySubscription(subscription_name);
    const subscriptionPlan: SubscriptionPlan = paymentType === PaymentType.PREPAID ? SubscriptionPlanValues.NONE : subscription_name;

    const parsedTimerSetting = parseTimerSetting(timerSetting);
    const activeMonitoringStartTime = timerSettingOff ? undefined : parsedTimerSetting.startTime;
    const activeMonitoringEndTime = timerSettingOff ? undefined : parsedTimerSetting.endTime;

    const timelapseDuration: Duration | undefined = timelapseSettingOff ? undefined : parseTimelapseDuration(timelapseSetting);

    return {
      imei,
      cameraMode,
      cameraModeOff,
      cameraModePending,
      sensitivity,
      sensitivityPending,
      nightMode,
      nightModePending,
      maxNumberPerHour,
      maxNumberPerHourPending,
      pictureNumberToSend,
      pictureNumberToSendPending,
      quickPhotoEnabled,
      quickPhotoEnabledPending,
      snapshotNumber,
      snapshotNumberPending,
      sendingPictureSize,
      sendingPictureSizePending,
      imageSize,
      imageSizePending,
      timerSetting,
      timerSettingPending,
      timerSettingOff,
      pauseCaptureDelay: delaySettingsDuration,
      pauseCaptureDelayPending: delaySettingPending,
      pauseCaptureDelayEnabled: !delaySettingOff,
      timelapseSetting,
      timelapseSettingPending,
      timelapseSettingOff,
      motionSensorSetting,
      controlPicture1,
      controlPicture1Pending,
      controlPicture1Off,
      controlPicture2,
      controlPicture2Pending,
      controlPicture2Off,
      sendReportInterval,
      sendReportIntervalPending,
      sendReportIntervalOff,
      instantMode,
      instantModePending,
      instantModeOff,
      cameraNameSetting,
      cameraNameString,
      cameraNameSettingPending,
      cameraNameSettingOff,
      secaTrack,
      secaTrackPending,
      secaTrackActive,
      secaTrackOff,
      gps,
      gpsPending,
      gpsOff,
      sdCycle,
      sdCyclePending,
      sdCycleOff,
      pinCodePending,
      resetPending,
      paymentType,
      subscriptionPlan,
      activeMonitoringStartTime,
      activeMonitoringEndTime,
      timelapseDuration,
    };
  };

  public static readonly toVideoSettings = (response: RemoteControlResponse): VideoSettingsState => {
    const pendingState = response.states.pendingState;
    const acknowledgedState = response.states.acknowledgedState;

    const videoSettings: VideoSettingsState = {
      videoRecordingEnabled: pendingState.cameraMode ? pendingState.cameraMode === CameraMode.ON : acknowledgedState.cameraMode === CameraMode.ON,
      videoRecordingPending: !!pendingState.cameraMode,

      videoTransmissionEnabled: pendingState.mobileVideo ? pendingState.mobileVideo === MobileVideo.ON : acknowledgedState.mobileVideo === MobileVideo.ON,
      videoTransmissionPending: !!pendingState.mobileVideo,

      videoSize: pendingState.videoSize ? pendingState.videoSize : acknowledgedState.videoSize,
      videoSizePending: !!pendingState.videoSize,

      videoTransmissionType: pendingState.videoMode ? pendingState.videoMode : acknowledgedState.videoMode,
      videoTransmissionTypePending: !!pendingState.videoMode,

      videoLength: pendingState.videoLength ? pendingState.videoLength : acknowledgedState.videoLength,
      videoLengthPending: !!pendingState.videoLength,
    };
    return videoSettings;
  };
}
